// Ask how many Corgi Packs to buy with Treats.

import React, { useEffect, useState } from "react";
import {
    CORGI_PACK_COST,
    clampCorgiPackGrantCount,
    formatTreats,
} from "../utils/treats";

export default function PackQuantityPrompt({
    treatBalance = 0,
    onConfirm,
    onCancel,
}) {
    const maxAffordable = Math.max(
        0,
        Math.min(25, Math.floor(Number(treatBalance) / CORGI_PACK_COST))
    );
    const [rawCount, setRawCount] = useState("1");

    useEffect(() => {
        setRawCount(maxAffordable > 0 ? "1" : "1");
    }, [maxAffordable, treatBalance]);

    const parsed = Math.floor(Number(rawCount));
    const packCount = Number.isFinite(parsed)
        ? clampCorgiPackGrantCount(parsed)
        : 0;
    const cost = packCount > 0 ? CORGI_PACK_COST * packCount : 0;
    const canBuy = packCount > 0 && treatBalance >= cost;

    const setCountValue = (nextCount) => {
        const clamped = clampCorgiPackGrantCount(nextCount);
        setRawCount(String(Math.max(1, clamped || 1)));
    };

    const adjustCount = (delta) => {
        const current = Number.isFinite(parsed) ? parsed : 1;
        setCountValue(current + delta);
    };

    const handleSubmit = () => {
        if (typeof onConfirm !== "function") {
            return;
        }
        onConfirm(Math.max(1, packCount || 1));
    };

    return (
        <div
            className="guideHeartDemoOverlay packQuantityPrompt"
            role="dialog"
            aria-modal="true"
            aria-labelledby="packQuantityPromptTitle"
        >
            <div className="guideHeartDemoOverlay__panel">
                <div
                    id="packQuantityPromptTitle"
                    className="guideHeartDemoOverlay__title"
                >
                    Buy Corgi Packs
                </div>
                <p className="guideHeartDemoOverlay__message">
                    Each pack costs {formatTreats(CORGI_PACK_COST)}. You have{" "}
                    {formatTreats(treatBalance)}.
                    {maxAffordable > 0
                        ? ` You can afford up to ${maxAffordable}.`
                        : " You cannot afford a pack right now."}
                </p>

                <label className="packQuantityPrompt__label" htmlFor="packQty">
                    How many packs?
                </label>
                <div className="packQuantityPrompt__stepper">
                    <button
                        type="button"
                        className="packQuantityPrompt__stepButton"
                        aria-label="Decrease pack count"
                        disabled={maxAffordable <= 0 || packCount <= 1}
                        onClick={() => adjustCount(-1)}
                    >
                        −
                    </button>
                    <input
                        id="packQty"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={99}
                        value={rawCount}
                        onChange={(event) => setRawCount(event.target.value)}
                        className="packQuantityPrompt__input"
                        disabled={maxAffordable <= 0}
                    />
                    <button
                        type="button"
                        className="packQuantityPrompt__stepButton"
                        aria-label="Increase pack count"
                        disabled={maxAffordable <= 0 || packCount >= maxAffordable}
                        onClick={() => adjustCount(1)}
                    >
                        +
                    </button>
                </div>

                <p className="packQuantityPrompt__total" aria-live="polite">
                    {packCount <= 0
                        ? "Enter a pack amount"
                        : canBuy
                          ? `Total · ${formatTreats(cost)}`
                          : `Need ${formatTreats(cost)} · you have ${formatTreats(
                                treatBalance
                            )}`}
                </p>

                <div className="packQuantityPrompt__actions">
                    <button
                        type="button"
                        className="homePrimaryButton"
                        disabled={packCount <= 0}
                        onClick={handleSubmit}
                    >
                        {packCount > 0
                            ? `Buy ${packCount} pack${packCount === 1 ? "" : "s"}`
                            : "Enter amount"}
                    </button>
                    <button
                        type="button"
                        className="homePrimaryButton homeSecondaryButton"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
